import { Link, useRouterState } from "@tanstack/react-router";
import { useEffect, useId, useRef, useState } from "react";
import { Logo } from "./Logo";
import { detectLocale, alternateOf, t, pathMap } from "@/lib/locale";




type Props = { variant?: "transparent" | "solid" };

type ServiceItem = { to: string; label: string; sub: string };
type ToolItem = { to: string; label: string; sub: string };

function getServices(locale: "de" | "en"): ServiceItem[] {
  return locale === "en"
    ? [
        { to: "/en/services/performance-marketing", label: "Performance Marketing", sub: "SEA, Shopping, Paid Social" },
        { to: "/en/services/social-media-marketing", label: "Social Media Marketing", sub: "Organic + Paid integrated" },
        { to: "/en/services/seo-ai-seo", label: "SEO & AI SEO", sub: "Visibility as a lever" },
        { to: "/en/services/geo-generative-engine-optimization", label: "GEO — Generative Engine Optimization", sub: "Visible in AI answers" },
        { to: "/en/services/conversion-optimization", label: "Conversion & Web Optimization", sub: "More from existing traffic" },
        { to: "/en/services/conversion-rate-optimization", label: "Conversion Rate Optimization", sub: "Hypothesis-driven" },
        { to: "/en/services/tracking-data-foundation", label: "Tracking & Data Foundation", sub: "Decisions you can trust" },
        { to: "/en/services/strategic-coordination", label: "Strategic Sparring & Coordination", sub: "Sparring as equals" },
      ]
    : [
        { to: "/leistungen/performance-marketing", label: "Performance Marketing", sub: "SEA, Shopping, Paid Social" },
        { to: "/leistungen/social-media-marketing", label: "Social Media Marketing", sub: "Organic + Paid integriert" },
        { to: "/leistungen/seo-ai-seo", label: "SEO & AI SEO", sub: "Sichtbarkeit als Hebel" },
        { to: "/leistungen/geo-generative-engine-optimization", label: "GEO — Generative Engine Optimization", sub: "Sichtbar in KI-Antworten" },
        { to: "/leistungen/conversion-web-optimierung", label: "Conversion- & Web-Optimierung", sub: "Mehr aus bestehendem Traffic" },
        { to: "/leistungen/conversion-rate-optimierung", label: "Conversion-Rate-Optimierung", sub: "Hypothesen-getrieben" },
        { to: "/leistungen/tracking-datenbasis", label: "Tracking & Datenbasis", sub: "Belastbare Entscheidungen" },
        { to: "/leistungen/strategie-koordination", label: "Strategie & Koordination", sub: "Sparring auf Augenhöhe" },
      ];
}

function getDevelopment(locale: "de" | "en"): ServiceItem[] {
  return locale === "en"
    ? [
        { to: "/en/services/website-development", label: "Website Development", sub: "CMS-driven sites" },
        { to: "/en/services/ecommerce-development", label: "E-Commerce Development", sub: "Selling systems" },
        { to: "/en/services/digital-development", label: "Digital Services Development", sub: "Apps, PWA & SaaS" },
      ]
    : [
        { to: "/leistungen/website-entwicklung", label: "Website-Entwicklung", sub: "CMS-getriebene Sites" },
        { to: "/leistungen/onlineshop-entwicklung", label: "Onlineshop-Entwicklung", sub: "Verkaufende Systeme" },
        { to: "/leistungen/entwicklung-digitaler-dienste", label: "Entwicklung digitaler Dienste", sub: "Apps, PWA & SaaS" },
      ];
}


function getTools(locale: "de" | "en"): ToolItem[] {
  return locale === "en"
    ? [
        { to: "/en/services/delta", label: "Delta", sub: "Product feed management" },
        { to: "/en/services/picazzoo", label: "picazzoo", sub: "AI-powered visual creation" },
        { to: "/en/services/gonzalads", label: "GonzalAds", sub: "Speedy ad management for Meta" },
      ]
    : [
        { to: "/leistungen/delta", label: "Delta", sub: "Produktfeed-Management" },
        { to: "/leistungen/picazzoo", label: "picazzoo", sub: "AI-Powered Visual Creation" },
        { to: "/leistungen/gonzalads", label: "GonzalAds", sub: "Speedy Ad Management für Meta" },
      ];
}

type PmItem = { to: string; label: string };

function getPmDisciplines(locale: "de" | "en"): PmItem[] {
  const base = locale === "en" ? "/en/services/performance-marketing" : "/leistungen/performance-marketing";
  const affiliate = locale === "en" ? "Affiliate Marketing" : "Affiliate-Marketing";
  return [
    { to: `${base}/google-ads`, label: "Google Ads" },
    { to: `${base}/meta-ads`, label: "Meta Ads" },
    { to: `${base}/linkedin-ads`, label: "LinkedIn Ads" },
    { to: `${base}/tiktok-ads`, label: "TikTok Ads" },
    { to: `${base}/microsoft-ads`, label: "Microsoft Ads" },
    { to: `${base}/youtube-demand-gen`, label: locale === "en" ? "YouTube & Demand Gen" : "YouTube & Demand Gen" },
    { to: `${base}/affiliate-marketing`, label: affiliate },
  ];
}

function ArrowRight() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden className="ml-1 inline-block">
      <path d="M5 12h14" />
      <path d="m12 5 7 7-7 7" />
    </svg>
  );
}

function ChevronDown({ open }: { open: boolean }) {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden className={`ml-1 inline-block transition-transform duration-200 ${open ? "rotate-180" : ""}`}>
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

export function Topbar({ variant = "transparent" }: Props) {
  const path = useRouterState({ select: (s) => s.location.pathname });
  // Pull alternateLocalePath from the deepest matched route's loaderData,
  // when present. Detail routes (blog) set this in their loader so the
  // sitewide chrome never needs to import MD-glob slug maps.
  const alternateFromMatch = useRouterState({
    select: (s) => {
      for (let i = s.matches.length - 1; i >= 0; i--) {
        const data = s.matches[i]?.loaderData as { alternateLocalePath?: string } | undefined;
        if (data?.alternateLocalePath) return data.alternateLocalePath;
      }
      return null;
    },
  });
  const locale = detectLocale(path);
  const tr = t[locale];
  const alt = alternateOf(path);
  const otherLocale = locale === "de" ? "en" : "de";
  let otherPath = otherLocale === "en" ? alt.en : alt.de;
  // Any route may override its alternate-locale path via loaderData.
  // Detail routes (blog + glossary) set this so the Topbar never needs
  // to import MD-glob slug maps. DE-only pages use it to redirect the
  // switcher to the nearest existing EN equivalent.
  if (alternateFromMatch) {
    otherPath = alternateFromMatch;
  }



  const services = getServices(locale);
  const development = getDevelopment(locale);
  const tools = getTools(locale);
  const pmDisciplines = getPmDisciplines(locale);
  const pmHubPath = locale === "en" ? "/en/services/performance-marketing" : "/leistungen/performance-marketing";
  const servicesHub = locale === "en" ? "/en/services" : "/leistungen";
  const toolsHub = locale === "en" ? "/en/services/tools" : "/leistungen/tools";
  const aboutHref = locale === "en" ? pathMap.about.en : pathMap.about.de;
  const contactHref = locale === "en" ? pathMap.contact.en : pathMap.contact.de;

  const labels =
    locale === "en"
      ? {
          servicesEyebrow: "Services",
          developmentEyebrow: "Development",
          toolsEyebrow: "Tools",
          overviewEyebrow: "Overview",
          allServices: "All services",
          allTools: "All tools at a glance",
          about: "About",
          contact: "Get in touch",
          ariaSubmenu: "Services submenu",
          pmEyebrow: "Performance disciplines",
          pmAll: "All performance marketing disciplines",
        }
      : {
          servicesEyebrow: "Leistungen",
          developmentEyebrow: "Entwicklung",
          toolsEyebrow: "Tools",
          overviewEyebrow: "Überblick",
          allServices: "Alle Leistungen",
          allTools: "Alle Tools im Überblick",
          about: "Über uns",
          contact: "Kontakt aufnehmen",
          ariaSubmenu: "Leistungs-Untermenü",
          pmEyebrow: "Performance-Disziplinen",
          pmAll: "Alle Performance-Marketing-Disziplinen",
        };

  const nav =
    locale === "en"
      ? [
          { to: pathMap.home.en, label: tr.nav.home, match: "/en", key: "home" as const },
          { to: pathMap.services.en, label: tr.nav.services, match: "/en/services", key: "services" as const },
          { to: pathMap["tools-hub"].en, label: tr.nav.tools, match: "/en/tools", key: "tools" as const },
          { to: pathMap.about.en, label: tr.nav.about, match: "/en/about", key: "about" as const },
          { to: pathMap.contact.en, label: tr.nav.contact, match: "/en/contact", key: "contact" as const },
        ]
      : [
          { to: pathMap.home.de, label: tr.nav.home, match: "/", key: "home" as const },
          { to: pathMap.services.de, label: tr.nav.services, match: "/leistungen", key: "services" as const },
          { to: pathMap["tools-hub"].de, label: tr.nav.tools, match: "/tools", key: "tools" as const },
          { to: pathMap.about.de, label: tr.nav.about, match: "/ueber-uns", key: "about" as const },
          { to: pathMap.contact.de, label: tr.nav.contact, match: "/kontakt", key: "contact" as const },
        ];

  const solid = variant === "solid";

  // Mega menu state (desktop)
  const [megaOpen, setMegaOpen] = useState(false);
  const [pmOpen, setPmOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [mobilePmOpen, setMobilePmOpen] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const firstItemRef = useRef<HTMLAnchorElement | null>(null);
  const pmTriggerRef = useRef<HTMLAnchorElement | null>(null);
  const focusPmFirstOnOpen = useRef(false);
  const submenuId = useId();

  function open() {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    setMegaOpen(true);
  }
  function scheduleClose() {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => {
      setMegaOpen(false);
      setPmOpen(false);
    }, 320);
  }
  function openPm() {
    setPmOpen(true);
  }
  function closePm() {
    setPmOpen(false);
  }

  function movePmFocus(currentIndex: number, direction: 1 | -1) {
    const items = Array.from(
      document.querySelectorAll<HTMLAnchorElement>("[data-pm-submenu-link]")
    );
    const next = items[currentIndex + direction];
    next?.focus();
  }

  // Close on Escape + click outside
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setMegaOpen(false);
        setPmOpen(false);
        setMobileOpen(false);
      }
    }
    function onClick(e: MouseEvent) {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target as Node)) {
        setMegaOpen(false);
        setPmOpen(false);
      }
    }
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
  }, []);

  // Close mega + mobile menu on route change
  useEffect(() => {
    setMegaOpen(false);
    setPmOpen(false);
    setMobileOpen(false);
    setMobileServicesOpen(false);
    setMobilePmOpen(false);
  }, [path]);

  useEffect(() => {
    if (!pmOpen || !focusPmFirstOnOpen.current) return;
    focusPmFirstOnOpen.current = false;
    document.querySelector<HTMLAnchorElement>("[data-pm-first-link]")?.focus();
  }, [pmOpen]);

  // Lock body scroll when mobile menu open
  useEffect(() => {
    if (mobileOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [mobileOpen]);

  // Scroll state: only meaningful for transparent variant
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    if (solid) {
      setIsScrolled(true);
      return;
    }
    const onScroll = () => setIsScrolled(window.scrollY > 80);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [solid]);

  const stuck = solid || isScrolled;
  const compactLogo = !solid && isScrolled;

  return (
    <header
      ref={wrapRef}
      className={`sticky top-0 z-50 transition-[background-color,backdrop-filter,border-color] duration-200 ease-out motion-reduce:transition-none ${
        solid ? "" : "-mb-[104px] md:-mb-[140px]"
      } ${
        stuck
          ? "border-b border-rule bg-white/85 backdrop-blur-md backdrop-saturate-150"
          : "border-b border-transparent bg-transparent"
      }`}
    >
      <div
        className={`mx-auto flex w-full max-w-[1600px] items-center px-6 md:px-12 lg:px-16 transition-[padding] duration-200 ease-out motion-reduce:transition-none ${
          stuck ? "py-3 md:py-4" : "py-6 md:py-7"
        }`}
      >
        {/* Left group: logo + nav */}
        <div className="flex items-center gap-10 lg:gap-14">
          <Link to={locale === "en" ? "/en" : "/"} className="inline-flex items-center">
            <Logo tone={stuck ? "ink" : "white"} compact={compactLogo || solid} />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden items-center gap-9 text-[18px] font-medium tracking-[-0.01em] lg:flex">
            {nav.map((n) => {
            const active =
              n.match === "/" || n.match === "/en"
                ? path === n.match
                : path.startsWith(n.match);
            const baseColor = stuck ? "text-ink" : "text-white";
            const opacity = active ? "opacity-100" : "opacity-80";
            if (n.key === "services") {
              return (
                <div
                  key={n.to}
                  className="relative"
                  onMouseEnter={open}
                  onMouseLeave={scheduleClose}
                >
                  <Link
                    to={n.to}
                    aria-haspopup="true"
                    aria-expanded={megaOpen}
                    aria-controls={submenuId}
                    onFocus={open}
                    onClick={() => setMegaOpen(false)}
                    className={`inline-flex items-center transition-colors hover:opacity-100 ${baseColor} ${opacity}`}
                  >
                    {n.label}
                    <ChevronDown open={megaOpen} />
                  </Link>
                  {/* Hover bridge to mega menu — prevents close when crossing gap */}
                  {megaOpen && (
                    <span
                      aria-hidden
                      className="absolute inset-x-0 top-full h-8"
                    />
                  )}
                </div>
              );
            }
            return (
              <Link
                key={n.to}
                to={n.to}
                className={`transition-colors hover:opacity-100 ${baseColor} ${opacity}`}
              >
                {n.label}
              </Link>
            );
          })}
          </nav>
        </div>

        {/* Right group: lang + CTA */}
        <div className="ml-auto hidden items-center gap-6 lg:flex">
          <Link
            to={otherPath}
            className={`font-mono text-[14px] uppercase tracking-wider transition-colors ${
              stuck ? "text-ink-3 hover:text-cyan-deep" : "text-white/85 hover:text-white"
            }`}
            aria-label={otherLocale === "en" ? "Switch to English" : "Zur deutschen Version wechseln"}
          >
            {otherLocale.toUpperCase()}
          </Link>
          <Link
            to={contactHref}
            className={`inline-flex items-center rounded-md bg-lime font-medium text-ink tracking-[-0.01em] transition-[padding,background-color] duration-200 ease-out hover:bg-lime-hover motion-reduce:transition-none ${
              stuck ? "px-5 py-2.5 text-[16px]" : "px-7 py-3.5 text-[17px]"
            }`}
          >
            {tr.cta}
          </Link>
        </div>

        {/* Mobile burger */}
        <button
          type="button"
          onClick={() => setMobileOpen((v) => !v)}
          aria-label={mobileOpen ? "Menü schließen" : "Menü öffnen"}
          aria-expanded={mobileOpen}
          className={`ml-auto inline-flex h-10 w-10 items-center justify-center rounded-md lg:hidden ${
            stuck ? "text-ink" : "text-white"
          }`}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            {mobileOpen ? (
              <>
                <path d="M18 6 6 18" />
                <path d="m6 6 12 12" />
              </>
            ) : (
              <>
                <path d="M3 12h18" />
                <path d="M3 6h18" />
                <path d="M3 18h18" />
              </>
            )}
          </svg>
        </button>
      </div>

      {/* Desktop Mega menu */}
      <div
        id={submenuId}
        role="navigation"
        aria-label={labels.ariaSubmenu}
        onMouseEnter={open}
        onMouseLeave={scheduleClose}
        className={`absolute inset-x-0 top-full hidden border-t border-rule bg-white shadow-[0_24px_40px_-24px_rgba(15,23,42,0.18)] motion-reduce:transition-none lg:block transition-[opacity,transform] duration-200 ease-out ${
          megaOpen
            ? "pointer-events-auto translate-y-0 opacity-100"
            : "pointer-events-none -translate-y-1 opacity-0"
        }`}
      >
        <div className="mx-auto grid max-w-[1440px] gap-12 px-12 py-12 lg:grid-cols-[1.4fr_1fr_0.9fr]">
          {/* Col 1 — Disciplines */}
          <div role="group" aria-labelledby="megamenu-services-label" className="relative">
            <p id="megamenu-services-label" className="sr-only">{labels.servicesEyebrow}</p>
            <p aria-hidden="true" className="mb-5 font-mono text-[11px] uppercase tracking-[0.18em] text-ink-4">
              {labels.servicesEyebrow}
            </p>
            <ul className="grid grid-cols-1 gap-1 sm:grid-cols-2">
              {services.map((s, i) => {
                const isPm = s.to === pmHubPath;
                return (
                  <li
                    key={s.to}
                    onMouseEnter={() => (isPm ? openPm() : closePm())}
                    onMouseLeave={isPm ? closePm : undefined}
                    onFocus={() => (isPm ? openPm() : closePm())}
                    className="relative"
                  >
                    <Link
                      ref={(node) => {
                        if (i === 0) firstItemRef.current = node;
                        if (isPm) pmTriggerRef.current = node;
                      }}
                      to={s.to}
                      aria-haspopup={isPm ? "menu" : undefined}
                      aria-expanded={isPm ? pmOpen : undefined}
                      onKeyDown={(e) => {
                        if (isPm && e.key === "ArrowDown") {
                          e.preventDefault();
                          focusPmFirstOnOpen.current = true;
                          openPm();
                        }
                        if (isPm && (e.key === "Escape" || e.key === "ArrowRight")) {
                          e.preventDefault();
                          closePm();
                        }
                      }}
                      className={`group block rounded-md px-3 py-2.5 transition-colors hover:bg-bg-subtle ${
                        isPm && pmOpen ? "bg-bg-subtle" : ""
                      }`}
                    >
                      <div className="flex items-center gap-1.5 text-[15px] font-medium text-ink group-hover:text-cyan-deep">
                        {s.label}
                        {isPm && (
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                            <path d="m9 6 6 6-6 6" />
                          </svg>
                        )}
                      </div>
                      <div className="mt-0.5 font-mono text-[11px] uppercase tracking-wider text-ink-4">
                        {s.sub}
                      </div>
                    </Link>
                    {isPm && pmOpen && (
                      <div
                        role="menu"
                        aria-label={labels.pmEyebrow}
                        className="absolute left-0 top-[calc(100%-1px)] z-20 w-full rounded-md border border-rule bg-white p-4 shadow-[0_18px_36px_-22px_rgba(15,23,42,0.22)]"
                      >
                        <p className="mb-3 font-mono text-[11px] uppercase tracking-[0.18em] text-ink-4">
                          {labels.pmEyebrow}
                        </p>
                        <ul className="space-y-0.5">
                          {pmDisciplines.map((p, pmIndex) => (
                            <li key={p.to}>
                              <Link
                                to={p.to}
                                role="menuitem"
                                data-pm-submenu-link=""
                                data-pm-first-link={pmIndex === 0 ? "" : undefined}
                                onKeyDown={(e) => {
                                  if (e.key === "ArrowDown") {
                                    e.preventDefault();
                                    movePmFocus(pmIndex, 1);
                                  }
                                  if (e.key === "ArrowUp") {
                                    e.preventDefault();
                                    movePmFocus(pmIndex, -1);
                                  }
                                  if (e.key === "Escape" || e.key === "ArrowRight") {
                                    e.preventDefault();
                                    closePm();
                                    pmTriggerRef.current?.focus();
                                  }
                                }}
                                className="group block rounded-md px-3 py-2 text-[14px] font-medium text-ink transition-colors hover:bg-bg-subtle hover:text-cyan-deep focus:bg-bg-subtle focus:text-cyan-deep focus:outline-none"
                              >
                                {p.label}
                              </Link>
                            </li>
                          ))}
                          <li className="mt-2 border-t border-rule pt-2">
                            <Link
                              to={pmHubPath}
                              role="menuitem"
                              data-pm-submenu-link=""
                              onKeyDown={(e) => {
                                if (e.key === "ArrowUp") {
                                  e.preventDefault();
                                  movePmFocus(pmDisciplines.length, -1);
                                }
                                if (e.key === "Escape" || e.key === "ArrowRight") {
                                  e.preventDefault();
                                  closePm();
                                  pmTriggerRef.current?.focus();
                                }
                              }}
                              className="inline-flex items-center rounded-md px-3 py-2 font-mono text-[11px] uppercase tracking-wider text-cyan-deep transition-colors hover:bg-bg-subtle focus:bg-bg-subtle focus:outline-none"
                            >
                              {labels.pmAll}
                              <ArrowRight />
                            </Link>
                          </li>
                        </ul>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Col 2 — Development + Tools */}
          <div className="space-y-8">
            <div role="group" aria-labelledby="megamenu-development-label">
              <p id="megamenu-development-label" className="sr-only">{labels.developmentEyebrow}</p>
              <p aria-hidden="true" className="mb-5 font-mono text-[11px] uppercase tracking-[0.18em] text-ink-4">
                {labels.developmentEyebrow}
              </p>
              <ul className="space-y-1">
                {development.map((d) => (
                  <li key={d.to}>
                    <Link
                      to={d.to}
                      className="group block rounded-md px-3 py-2.5 transition-colors hover:bg-bg-subtle"
                    >
                      <div className="text-[15px] font-medium text-ink group-hover:text-cyan-deep">
                        {d.label}
                      </div>
                      <div className="mt-0.5 font-mono text-[11px] uppercase tracking-wider text-ink-4">
                        {d.sub}
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div role="group" aria-labelledby="megamenu-tools-label">
              <p id="megamenu-tools-label" className="sr-only">{labels.toolsEyebrow}</p>
              <p aria-hidden="true" className="mb-5 font-mono text-[11px] uppercase tracking-[0.18em] text-ink-4">
                {labels.toolsEyebrow}
              </p>
              <ul className="space-y-1">
                {tools.map((tool) => (
                  <li key={tool.to}>
                    <Link
                      to={tool.to}
                      className="group block rounded-md px-3 py-2.5 transition-colors hover:bg-bg-subtle"
                    >
                      <div className="text-[15px] font-medium text-ink group-hover:text-cyan-deep">
                        {tool.label}
                      </div>
                      <div className="mt-0.5 font-mono text-[11px] uppercase tracking-wider text-ink-4">
                        {tool.sub}
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
              <div className="mt-4 border-t border-rule pt-4">
                <Link
                  to={toolsHub}
                  className="inline-flex items-center px-3 font-mono text-xs uppercase tracking-wider text-cyan-deep hover:underline"
                >
                  {labels.allTools}
                  <ArrowRight />
                </Link>
              </div>
            </div>
          </div>

          {/* Col 3 — Overview */}
          <div role="group" aria-labelledby="megamenu-overview-label">
            <p id="megamenu-overview-label" className="sr-only">{labels.overviewEyebrow}</p>
            <p aria-hidden="true" className="mb-5 font-mono text-[11px] uppercase tracking-[0.18em] text-ink-4">
              {labels.overviewEyebrow}
            </p>
            <ul className="space-y-2">
              <li>
                <Link
                  to={servicesHub}
                  className="inline-flex items-center rounded-md px-3 py-2 text-[15px] font-semibold text-cyan-deep transition-colors hover:bg-bg-subtle"
                >
                  {labels.allServices}
                  <ArrowRight />
                </Link>
              </li>
              <li>
                <Link
                  to={aboutHref}
                  className="block rounded-md px-3 py-2 text-[15px] text-ink transition-colors hover:bg-bg-subtle"
                >
                  {labels.about}
                </Link>
              </li>
            </ul>
            <div className="mt-6">
              <Link
                to={contactHref}
                className="inline-flex items-center rounded-md bg-lime px-4 py-2 text-sm font-semibold text-ink transition-colors hover:bg-lime-hover"
              >
                {labels.contact}
              </Link>
            </div>
          </div>

        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="fixed inset-x-0 top-[72px] z-40 max-h-[calc(100vh-72px)] overflow-y-auto border-t border-rule bg-white lg:hidden">
          <nav className="mx-auto max-w-[1200px] px-6 py-6">
            <ul className="space-y-1 text-ink">
              {nav.map((n) => {
                if (n.key === "services") {
                  return (
                    <li key={n.to}>
                      <button
                        type="button"
                        onClick={() => setMobileServicesOpen((v) => !v)}
                        aria-expanded={mobileServicesOpen}
                        className="flex w-full items-center justify-between px-3 py-3 text-left text-base font-medium"
                      >
                        <span>{n.label}</span>
                        <ChevronDown open={mobileServicesOpen} />
                      </button>
                      {mobileServicesOpen && (
                        <div className="ml-3 border-l border-rule pl-4 pb-2">
                          <Link
                            to={servicesHub}
                            className="block py-2 font-mono text-xs uppercase tracking-wider text-cyan-deep"
                          >
                            {labels.allServices} →
                          </Link>
                          <ul className="mt-1 space-y-0.5">
                            {services.map((s) => {
                              const isPm = s.to === pmHubPath;
                              return (
                                <li key={s.to}>
                                  {isPm ? (
                                    <>
                                      <button
                                        type="button"
                                        onClick={() => setMobilePmOpen((v) => !v)}
                                        aria-expanded={mobilePmOpen}
                                        className="flex w-full items-center justify-between py-2 text-left text-[15px] text-ink"
                                      >
                                        <span>{s.label}</span>
                                        <ChevronDown open={mobilePmOpen} />
                                      </button>
                                      {mobilePmOpen && (
                                        <div className="ml-2 border-l border-rule pl-3 pb-1">
                                          <ul className="space-y-0.5">
                                            {pmDisciplines.map((p) => (
                                              <li key={p.to}>
                                                <Link to={p.to} className="block py-1.5 text-[14px] text-ink-2">
                                                  {p.label}
                                                </Link>
                                              </li>
                                            ))}
                                            <li>
                                              <Link
                                                to={pmHubPath}
                                                className="block py-1.5 font-mono text-[11px] uppercase tracking-wider text-cyan-deep"
                                              >
                                                {labels.pmAll} →
                                              </Link>
                                            </li>
                                          </ul>
                                        </div>
                                      )}
                                    </>
                                  ) : (
                                    <Link to={s.to} className="block py-2 text-[15px] text-ink">
                                      {s.label}
                                    </Link>
                                  )}
                                </li>
                              );
                            })}
                          </ul>
                          <div className="mt-3 border-t border-rule pt-3">
                            <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-4">
                              {labels.developmentEyebrow}
                            </div>
                            <ul className="mt-1 space-y-0.5">
                              {development.map((d) => (
                                <li key={d.to}>
                                  <Link to={d.to} className="block py-2 text-[15px] text-ink">
                                    {d.label}
                                  </Link>
                                </li>
                              ))}
                            </ul>
                          </div>
                          <div className="mt-3 border-t border-rule pt-3">
                            <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-4">
                              {labels.toolsEyebrow}
                            </div>
                            <ul className="mt-1 space-y-0.5">
                              {tools.map((tool) => (
                                <li key={tool.to}>
                                  <Link to={tool.to} className="block py-2 text-[15px] text-ink">
                                    {tool.label}
                                  </Link>
                                </li>
                              ))}
                              <li>
                                <Link
                                  to={toolsHub}
                                  className="block py-2 font-mono text-xs uppercase tracking-wider text-cyan-deep"
                                >
                                  {labels.allTools} →
                                </Link>
                              </li>
                            </ul>
                          </div>
                        </div>
                      )}
                    </li>
                  );
                }
                return (
                  <li key={n.to}>
                    <Link to={n.to} className="block px-3 py-3 text-base font-medium">
                      {n.label}
                    </Link>
                  </li>
                );
              })}
            </ul>
            <div className="mt-6 flex items-center justify-between gap-4 border-t border-rule pt-6">
              <Link
                to={otherPath}
                className="font-mono text-xs uppercase tracking-wider text-ink-3"
              >
                {otherLocale.toUpperCase()}
              </Link>
              <Link
                to={contactHref}
                className="inline-flex items-center rounded-md bg-lime px-4 py-2 text-sm font-semibold text-ink"
              >
                {tr.cta}
              </Link>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
